/**
 * Hook pour gérer les utilisateurs
 * @module admin/hooks/useUsers
 */

import { useState, useEffect, useCallback } from 'react';
import {
  getUsers,
  getUsersStats,
  createUser,
  updateUser,
  deleteUser,
  bulkDeleteUsers,
  activateUser,
  changeUserPassword,
} from '../services/usersService';
import {
  DEFAULT_USERS_PAGINATION,
  DEFAULT_USERS_FILTERS,
} from '../constants/defaultValues';

/**
 * Hook de gestion des utilisateurs
 * @returns {Object} État et fonctions pour les utilisateurs
 */
export const useUsers = () => {
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState(null);
  const [usersPagination, setUsersPagination] = useState(DEFAULT_USERS_PAGINATION);
  const [usersFilters, setUsersFilters] = useState(DEFAULT_USERS_FILTERS);
  const [usersStats, setUsersStats] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editUserModal, setEditUserModal] = useState(false);
  const [editUserData, setEditUserData] = useState({});
  const [createUserModal, setCreateUserModal] = useState(false);
  const [createUserData, setCreateUserData] = useState({
    username: '',
    email: '',
    name: '',
    password: '',
    role: 'viewer',
    isActive: true,
  });
  const [changePasswordModal, setChangePasswordModal] = useState(false);
  const [changePasswordUser, setChangePasswordUser] = useState(null);
  const [changePasswordValue, setChangePasswordValue] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState([]);

  /**
   * Récupère la liste des utilisateurs
   */
  const fetchUsers = useCallback(async () => {
    setUsersLoading(true);
    setUsersError(null);

    try {
      const params = {
        page: usersPagination.page,
        limit: usersPagination.limit,
        search: usersFilters.search,
        role: usersFilters.role,
        isActive: usersFilters.isActive,
      };
      const data = await getUsers(params);
      if (data.success) {
        const nextUsers = data.data.users;
        setUsers(nextUsers);
        setUsersPagination(data.data.pagination);
        setSelectedUserIds((prev) =>
          prev.filter((id) => nextUsers.some((u) => String(u.id) === String(id)))
        );
      }
    } catch (err) {
      setUsersError(err.message);
    } finally {
      setUsersLoading(false);
    }
  }, [usersPagination.page, usersPagination.limit, usersFilters.search, usersFilters.role, usersFilters.isActive]);

  /**
   * Récupère les statistiques des utilisateurs
   */
  const fetchUsersStats = useCallback(async () => {
    try {
      const data = await getUsersStats();
      if (data.success) {
        setUsersStats(data.data);
      }
    } catch (err) {
      console.error('Erreur lors de la récupération des statistiques utilisateurs:', err);
    }
  }, []);

  /**
   * Gère le changement d'un filtre
   * @param {string} field - Nom du champ
   * @param {*} value - Nouvelle valeur
   */
  const handleUserFilterChange = useCallback((field, value) => {
    setUsersFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
    setUsersPagination((prev) => ({
      ...prev,
      page: 1, // Réinitialiser à la première page lors d'un changement de filtre
    }));
  }, []);

  /**
   * Gère le changement de page
   * @param {number} newPage - Nouvelle page
   */
  const handleUserPageChange = useCallback((newPage) => {
    setUsersPagination((prev) => ({
      ...prev,
      page: newPage,
    }));
  }, []);

  /**
   * Crée un nouvel utilisateur
   */
  const handleCreateUser = useCallback(async () => {
    if (!createUserData.username?.trim()) {
      setUsersError("Le nom d'utilisateur est requis");
      return;
    }
    if (!createUserData.password || createUserData.password.length < 6) {
      setUsersError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    setUsersLoading(true);
    setUsersError(null);

    try {
      const data = await createUser(createUserData);
      if (data.success) {
        setCreateUserModal(false);
        setCreateUserData({
          username: '',
          email: '',
          name: '',
          password: '',
          role: 'viewer',
          isActive: true,
        });
        await fetchUsers();
        await fetchUsersStats();
      }
    } catch (err) {
      setUsersError(err.message || err.error || 'Erreur lors de la création');
    } finally {
      setUsersLoading(false);
    }
  }, [createUserData, fetchUsers, fetchUsersStats]);

  /**
   * Ouvre le modal d'édition pour un utilisateur
   * @param {Object} user - Utilisateur à éditer
   */
  const handleEditUser = useCallback((user) => {
    setSelectedUser(user);
    setEditUserData({
      role: user.role || '',
      isActive: user.isActive !== undefined ? user.isActive : true,
      email: user.email || '',
      name: user.name || '',
      twitchLogin: user.twitchLogin || '',
      twitchDisplayName: user.twitchDisplayName || '',
      profileImageUrl: user.profileImageUrl || '',
      isPremium: user.isPremium === true,
      premiumExpiresAt: user.premiumExpiresAt || '',
    });
    setEditUserModal(true);
  }, []);

  /**
   * Sauvegarde les modifications d'un utilisateur
   */
  const handleSaveUser = useCallback(async () => {
    if (!selectedUser) return;

    setUsersLoading(true);
    setUsersError(null);

    const payload = { ...editUserData };
    if (payload.isPremium && payload.premiumExpiresAt === '') {
      payload.premiumExpiresAt = null;
    }

    try {
      const data = await updateUser(selectedUser.id, payload);
      if (data.success) {
        setEditUserModal(false);
        setSelectedUser(null);
        await fetchUsers();
        await fetchUsersStats();
      }
    } catch (err) {
      setUsersError(err.message);
    } finally {
      setUsersLoading(false);
    }
  }, [selectedUser, editUserData, fetchUsers, fetchUsersStats]);

  /**
   * Supprime ou désactive un utilisateur
   * @param {string} userId - ID de l'utilisateur
   * @param {boolean} hardDelete - Si true, suppression définitive
   */
  const handleDeleteUser = useCallback(async (userId, hardDelete = false) => {
    if (!window.confirm(
      hardDelete
        ? 'Êtes-vous sûr de vouloir supprimer définitivement cet utilisateur ? Cette action est irréversible.'
        : 'Êtes-vous sûr de vouloir désactiver cet utilisateur ?'
    )) {
      return;
    }

    setUsersLoading(true);
    setUsersError(null);

    try {
      const data = await deleteUser(userId, hardDelete);
      if (data.success) {
        setSelectedUserIds((prev) => prev.filter((id) => String(id) !== String(userId)));
        await fetchUsers();
        await fetchUsersStats();
      }
    } catch (err) {
      setUsersError(err.message);
    } finally {
      setUsersLoading(false);
    }
  }, [fetchUsers, fetchUsersStats]);

  /**
   * Ouvre le modal pour changer le mot de passe
   * @param {Object} user - Utilisateur concerné
   */
  const handleOpenChangePassword = useCallback((user) => {
    setChangePasswordUser(user);
    setChangePasswordValue('');
    setChangePasswordModal(true);
  }, []);

  /**
   * Change le mot de passe d'un utilisateur
   */
  const handleChangePassword = useCallback(async () => {
    if (!changePasswordUser) return;
    if (!changePasswordValue || changePasswordValue.length < 6) {
      setUsersError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    setUsersLoading(true);
    setUsersError(null);

    try {
      const data = await changeUserPassword(changePasswordUser.id, changePasswordValue);
      if (data.success) {
        setChangePasswordModal(false);
        setChangePasswordUser(null);
        setChangePasswordValue('');
      }
    } catch (err) {
      setUsersError(err.message);
    } finally {
      setUsersLoading(false);
    }
  }, [changePasswordUser, changePasswordValue]);

  /**
   * Réactive un utilisateur
   * @param {string} userId - ID de l'utilisateur
   */
  const handleActivateUser = useCallback(async (userId) => {
    setUsersLoading(true);
    setUsersError(null);

    try {
      const data = await activateUser(userId);
      if (data.success) {
        await fetchUsers();
        await fetchUsersStats();
      }
    } catch (err) {
      setUsersError(err.message);
    } finally {
      setUsersLoading(false);
    }
  }, [fetchUsers, fetchUsersStats]);

  const toggleUserRowSelected = useCallback((userId) => {
    setSelectedUserIds((prev) => {
      const sid = String(userId);
      const has = prev.some((id) => String(id) === sid);
      if (has) return prev.filter((id) => String(id) !== sid);
      return [...prev, userId];
    });
  }, []);

  const toggleSelectAllUsersOnPage = useCallback(() => {
    setSelectedUserIds((prev) => {
      const pageIds = users.map((u) => u.id);
      const allOnPageSelected =
        pageIds.length > 0 &&
        pageIds.every((id) => prev.some((p) => String(p) === String(id)));
      if (allOnPageSelected) {
        return prev.filter(
          (id) => !pageIds.some((pid) => String(pid) === String(id))
        );
      }
      const byKey = new Map();
      [...prev, ...pageIds].forEach((id) => {
        byKey.set(String(id), id);
      });
      return [...byKey.values()];
    });
  }, [users]);

  const handleBulkDeleteUsers = useCallback(async () => {
    if (selectedUserIds.length === 0) return;
    if (selectedUserIds.length > 50) {
      setUsersError('Sélectionnez au maximum 50 comptes à supprimer');
      return;
    }
    if (
      !window.confirm(
        `Supprimer définitivement ${selectedUserIds.length} compte(s) ? Cette action est irréversible (RGPD).`
      )
    ) {
      return;
    }
    const typed = window.prompt(
      'Pour confirmer, saisissez exactement : SUPPRIMER'
    );
    if (typed !== 'SUPPRIMER') {
      if (typed !== null) {
        setUsersError('Confirmation incorrecte : suppression annulée');
      }
      return;
    }

    setUsersLoading(true);
    setUsersError(null);

    try {
      const data = await bulkDeleteUsers(selectedUserIds.map((id) => Number(id)));
      if (data.success) {
        setSelectedUserIds([]);
        await fetchUsers();
        await fetchUsersStats();
        if (data.data?.failed?.length) {
          setUsersError(
            data.message ||
              `${data.data.failed.length} suppression(s) ont échoué — voir la console admin.`
          );
        }
      }
    } catch (err) {
      setUsersError(err.message);
    } finally {
      setUsersLoading(false);
    }
  }, [selectedUserIds, fetchUsers, fetchUsersStats]);

  // Recharger les utilisateurs quand la pagination ou les filtres changent
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  return {
    users,
    usersLoading,
    usersError,
    usersPagination,
    usersFilters,
    usersStats,
    selectedUser,
    editUserModal,
    editUserData,
    setEditUserData,
    setEditUserModal,
    createUserModal,
    createUserData,
    setCreateUserData,
    setCreateUserModal,
    changePasswordModal,
    changePasswordUser,
    changePasswordValue,
    setChangePasswordValue,
    setChangePasswordModal,
    setChangePasswordUser,
    fetchUsers,
    fetchUsersStats,
    handleUserFilterChange,
    handleUserPageChange,
    handleCreateUser,
    handleEditUser,
    handleSaveUser,
    handleDeleteUser,
    handleOpenChangePassword,
    handleChangePassword,
    handleActivateUser,
    selectedUserIds,
    toggleUserRowSelected,
    toggleSelectAllUsersOnPage,
    handleBulkDeleteUsers,
  };
};
